import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import { Users, X, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import CsvUploadTab from "./tabs/csvUploadTab";
import ManualSelectionTab from "./tabs/manualSelectionTab";
import { StepHelpers } from "@shared/components/Stepper";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

interface SelectedConsumer {
  id: string;
  consumerNo: string;
  firstName: string;
  lastName: string;
  zone?: string;
  statusMessage?: string;
}

interface SelectConsumersStepProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep: (stepName: string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onSelectionChange?: (count: number) => void;
}

export const SelectConsumersStep: React.FC<SelectConsumersStepProps> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onSelectionChange,
}) => {
  const [selectedConsumers, setSelectedConsumers] = useState<
    SelectedConsumer[]
  >([]);

  // Load data from stepper persistence on mount
  useEffect(() => {
    if (stepHelpers) {
      const savedData = stepHelpers.getStepData(currentStepIndex);
      if (savedData?.selectedConsumers) {
        setSelectedConsumers(savedData.selectedConsumers);
        onSelectionChange?.(savedData.selectedConsumers.length);
      }
    }
  }, [stepHelpers, currentStepIndex, onSelectionChange]);

  useEffect(() => {
    onSelectionChange?.(selectedConsumers.length);
  }, [selectedConsumers.length, onSelectionChange]);

  const updatePersistence = (consumers: SelectedConsumer[]) => {
    if (stepHelpers) {
      stepHelpers.setStepData(currentStepIndex, {
        selectedConsumers: consumers,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleAddConsumers = (newConsumers: SelectedConsumer[]) => {
    setSelectedConsumers((prev) => {
      const existingIds = new Set(prev.map((c) => String(c.id)));
      const filteredNew = newConsumers.filter((c) => !existingIds.has(String(c.id)));
      const updated = [...prev, ...filteredNew];
      updatePersistence(updated);
      return updated;
    });
  };

  const handleRemove = (consumer: SelectedConsumer) => {
    setSelectedConsumers((prev) => {
      const updated = prev.filter((c) => String(c.id) !== String(consumer.id));
      updatePersistence(updated);
      return updated;
    });
  };

  const handleClearAll = () => {
    const updated: SelectedConsumer[] = [];
    setSelectedConsumers(updated);
    updatePersistence(updated);
  };

  const handleNextStep = () => {
    if (selectedConsumers.length < 2) {
      return;
    }
    logTimeUntilStep?.("Select Consumers");
    onNext?.();
  };

  const handleToggleManualConsumer = (
    consumer: SelectedConsumer,
    isSelected: boolean,
  ) => {
    if (isSelected) {
      handleAddConsumers([consumer]);
    } else {
      handleRemove(consumer);
    }
  };

  const columns: TableColumn<SelectedConsumer>[] = [
    {
      header: "Account No",
      key: "consumerNo",
      width: "w-[150px]",
      render: (value: any, row: any) => value || row?.consumer_no || "N/A",
    },
    {
      header: "Name",
      key: "firstName",
      render: (value: string, row: any) => {
        const first = value || row?.first_name || "";
        const last = row?.lastName || row?.last_name || "";
        return `${first} ${last}`.trim() || "N/A";
      },
    },
    {
      header: "Phone",
      key: "contactNumber",
      render: (value: any, row: any) =>
        value || row?.contact_number || row?.phone || "N/A",
    },
    {
      header: "Email",
      key: "email",
      render: (value: any) => value || "N/A",
    },
    {
      header: "Premise",
      key: "premise",
      render: (value: any, row: any) =>
        value ||
        row?.premise_name ||
        row?.zone ||
        row?.territoryData?.service?.premise ||
        "N/A",
    },
    {
      header: "Status",
      key: "statusMessage",
      render: (value: any, row: any) => {
        const status =
          value ||
          row?.status_message ||
          row?.statusDisplay ||
          row?.status_message ||
          "N/A";
        return <Badge className={getStatusBadgeClass(status)}>{status}</Badge>;
      },
    },
  ];

  const actions: TableAction<SelectedConsumer>[] = [
    {
      label: "Remove",
      icon: <X className="h-4 w-4 text-red-500" />,
      onClick: (row: SelectedConsumer) => handleRemove(row),
    },
  ];

  const urlMapping = {
    csvUpload: "csvUpload",
    manualSelection: "manualSelection",
  };

  const tabComponents = {
    csvUpload: {
      label: "CSV Upload",
      component: <CsvUploadTab onAddConsumers={handleAddConsumers} />,
    },
    manualSelection: {
      label: "Manual Selection",
      component: (
        <ManualSelectionTab
          remoteUtilityId={remoteUtilityId}
          onAddConsumers={handleAddConsumers}
          selectedIds={selectedConsumers.map((c) => String(c.id))}
          onToggleConsumer={handleToggleManualConsumer}
        />
      ),
    },
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Selection Section */}
      <div className="p-4 border rounded-lg bg-white shadow-sm border-blue-100">
        <div className="flex gap-4 items-center mb-1">
          <div className="bg-blue-50 p-2 rounded-lg">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-bold text-neutral-800 tracking-tight">
            Consumer Selection
          </h3>
        </div>
        <div className="mt-4">
          <Tabs
            defaultValue="csvUpload"
            className="w-full"
            tabsListClassName="grid grid-cols-2 w-full bg-gradient-to-r from-blue-100/50 to-indigo-100/50 shadow-sm"
            urlMapping={urlMapping}
            tabComponents={tabComponents}
            level={1}
          />
        </div>
      </div>

      {/* Review Section */}
      <div className="p-5 border rounded-xl bg-white shadow-sm border-neutral-200">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
            <h3 className="text-md font-bold text-neutral-800 uppercase tracking-wider text-xs">
              Selected Consumers
            </h3>
            <Badge
              variant="secondary"
              className="bg-blue-50 text-blue-700 font-bold border-blue-100"
            >
              {selectedConsumers.length}
            </Badge>
          </div>
          {selectedConsumers.length > 0 && (
            <Button
              variant="link"
              className="text-red-500 hover:text-red-600 h-auto p-0 text-xs font-bold uppercase tracking-tighter"
              onClick={handleClearAll}
            >
              Clear All
            </Button>
          )}
        </div>

        <div className="border border-neutral-100 rounded-lg overflow-y-auto max-h-[400px]">
          <GenericTable<SelectedConsumer>
            data={selectedConsumers}
            columns={columns}
            actions={actions}
            actionsType="icons"
            rowKey="id"
            emptyMessage="No consumers selected yet. Use the search or upload options above."
            getRowClassName={() => "hover:bg-blue-50/20 transition-colors"}
          />
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="flex justify-end pt-4 border-t">
        <div className="flex items-center gap-4">
          {selectedConsumers.length < 2 && selectedConsumers.length > 0 && (
            <span className="text-xs text-orange-600 font-medium italic">
              * At least 2 consumers required to proceed
            </span>
          )}
          <Button
            onClick={handleNextStep}
            disabled={selectedConsumers.length < 2}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 px-6"
          >
            <span>Next: Request Details</span>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};
